// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __awaiter, __decorate } from 'tslib';
import { html } from 'lit';
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js';
import { getCalculatedBaseFontSize } from '../../utils/base-font-size.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { ShuiLitElement } from '../../utils/event-decorator.js';
import { keyboardInteraction } from '../../utils/keyboardInteraction.js';
import { KEYCODE } from '../../utils/keycode.js';
import navGroupStyles from './sh-nav-group.lit.scss.js';
import { SHNavItem } from './sh-nav-item.js';
let ShNavGroup = class ShNavGroup extends ShuiLitElement {
  constructor() {
    super(...arguments);
    /** Sets the aria-label for the nav tag for accessibility. */
    this.accessibleNavLabel = '';
    /** The index of the currently active navigation item within the group.*/
    this.activeIndex = '';
    /** Indicates whether the left icon to scroll left should be displayed.*/
    this.showLeft = false;
    /** Indicates whether the right icon to scroll right should be displayed.*/
    this.showRight = false;
    /** Indicates whether the navigation item is focused using the Tab key.*/
    this.focusByTab = false;
    this.onSelectHandler = (e) => this.onSelectListner(e);
    this.onNavScrollOrResizeHandler = () => this.updateArrowVisibility();
    this.handleNavScrollFocusIn = (e) => {
      const target = e.target;
      if (target instanceof SHNavItem && this.focusByTab && !target.disabled) {
        requestAnimationFrame(() => {
          this.scrollItemIntoView(target);
        });
        this.focusByTab = false;
      }
    };
    this.handleNavScrollKeydown = (e) => {
      this.focusByTab = keyboardInteraction(e, KEYCODE.TAB);
    };
  }
  static get styles() {
    return [navGroupStyles];
  }
  render() {
    return html`
      <div
        class="nav-container ${this.showLeft ? 'fade-left' : ''} ${this.showRight
          ? 'fade-right'
          : ''}"
      >
        ${this.showLeft
          ? html`<sh-icon
              button
              class="nav-arrow nav-arrow-left"
              icon="arrow-line-left-s"
              aria-label="Scroll navigation left"
              @click=${() => this.scrollNav('left')}
            ></sh-icon>`
          : null}
        <nav class="nav-scroll" aria-label="${this.accessibleNavLabel}">
          <slot @slotchange=${this.handleSlotChange}></slot>
        </nav>
        ${this.showRight
          ? html`<sh-icon
              button
              class="nav-arrow nav-arrow-right"
              icon="arrow-line-right-s"
              aria-label="Scroll navigation right"
              @click=${() => this.scrollNav('right')}
            ></sh-icon>`
          : null}
      </div>
    `;
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('on-select', this.onSelectHandler);
  }
  firstUpdated(changedProperties) {
    const _super = Object.create(null, {
      firstUpdated: { get: () => super.firstUpdated },
    });
    return __awaiter(this, void 0, void 0, function* () {
      _super.firstUpdated.call(this, changedProperties);
      if (deviceIdentifier.isTouchDevice()) {
        this.classList.add('touch-device');
      }
      this.navScroll.addEventListener('keydown', this.handleNavScrollKeydown);
      this.navScroll.addEventListener('scroll', this.onNavScrollOrResizeHandler, { passive: true });
      window.addEventListener('resize', this.onNavScrollOrResizeHandler);
      this.navScroll.addEventListener('focusin', this.handleNavScrollFocusIn);
      this.observeResizeElements();
      yield this.updateComplete;
      this.scrollActiveItemIntoView();
    });
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    this.removeEventListener('on-select', this.onSelectHandler);
    this.navScroll.removeEventListener('scroll', this.onNavScrollOrResizeHandler);
    window.removeEventListener('resize', this.onNavScrollOrResizeHandler);
    this.navScroll.removeEventListener('keydown', this.handleNavScrollKeydown);
    this.navScroll.removeEventListener('focusin', this.handleNavScrollFocusIn);
    (_a = this.navResizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
  }
  handleSlotChange() {
    return __awaiter(this, void 0, void 0, function* () {
      if (!this.items || this.items.length === 0) {
        this.activeIndex = undefined;
      }
      this.assignIdsToItems();
      this.updateActiveItemStates();
      yield this.updateComplete;
      this.updateArrowVisibility();
    });
  }
  /**
   * Updates the active item state based on the current activeIndex.
   * If no activeIndex is set, it defaults to the first item.
   */
  updateActiveItemStates() {
    var _a, _b, _c;
    if (!((_a = this.items) === null || _a === void 0 ? void 0 : _a.length)) return;
    if (!this.activeIndex) {
      this.activeIndex = this.items[0].navId;
    }
    // Deactivate previous active item
    if (this.activeItem && this.activeItem.navId !== this.activeIndex) {
      this.activeItem.active = false;
      (_b = this.navResizeObserver) === null || _b === void 0
        ? void 0
        : _b.unobserve(this.activeItem);
    }
    // Activate new active item
    this.activeItem = this.items.find((item) => item.navId === this.activeIndex && !item.disabled);
    if (this.activeItem) {
      this.activeItem.active = true;
      (_c = this.navResizeObserver) === null || _c === void 0
        ? void 0
        : _c.observe(this.activeItem);
      requestAnimationFrame(() => {
        this.updateActiveStroke(this.activeItem);
        this.scrollActiveItemIntoView();
      });
    }
  }
  /**
   * Observes resize events on navigation elements and updates the active stroke accordingly.
   * When scale is changed at runtime, this ensures the active item stroke is recalculated.
   * This is particularly useful for responsive designs where the layout may change.
   */
  observeResizeElements() {
    if (!this.navResizeObserver) {
      this.navResizeObserver = new ResizeObserver(() => {
        this.activeItem && this.updateActiveStroke(this.activeItem);
      });
    }
    if (this.navScroll) {
      this.navResizeObserver.observe(this.navScroll);
    }
    if (this.activeItem) {
      this.navResizeObserver.observe(this.activeItem);
    }
  }
  updateActiveStroke(activeItem) {
    if (!this.navScroll || !activeItem) return;
    const navRect = this.navScroll.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    const left = itemRect.left - navRect.left + this.navScroll.scrollLeft;
    const width = itemRect.width;
    this.navScroll.style.setProperty('--underline-left', `${left}px`);
    this.navScroll.style.setProperty('--underline-width', `${width}px`);
  }
  scrollItemIntoView(item) {
    var _a, _b;
    if (!this.navScroll) return;
    const navRect = this.navScroll.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const rightArrowWidth = this.showRight
      ? (_b = (_a = this.rightArrow) === null || _a === void 0 ? void 0 : _a.offsetWidth) !==
          null && _b !== void 0
        ? _b
        : 0
      : 0;
    const fadeWidth =
      parseInt(getComputedStyle(this.navScroll).getPropertyValue('--sh-nav-item-fade-width'), 10) *
        getCalculatedBaseFontSize() || 0;
    const visibleLeft = navRect.left + fadeWidth;
    const visibleRight = navRect.right - rightArrowWidth - fadeWidth;
    if (itemRect.left >= visibleLeft && itemRect.right <= visibleRight) return;
    let scrollDelta = 0;
    if (itemRect.left < visibleLeft) {
      scrollDelta = itemRect.left - visibleLeft;
    } else if (itemRect.right > visibleRight) {
      scrollDelta = itemRect.right - visibleRight;
    }
    if (scrollDelta !== 0) {
      this.navScroll.scrollBy({
        left: scrollDelta,
        behavior: 'smooth',
      });
    }
  }
  scrollActiveItemIntoView() {
    if (this.activeItem) {
      this.scrollItemIntoView(this.activeItem);
    }
  }
  assignIdsToItems() {
    if (!this.items) return;
    const currentActiveItem = this.activeItem;
    this.items.forEach((item, idx) => {
      item.navId = idx.toString();
    });
    if (currentActiveItem && this.items.includes(currentActiveItem)) {
      this.activeIndex = currentActiveItem.navId;
    }
  }
  onSelectListner(e) {
    const id = e.detail.id;
    if (!id || id === this.activeIndex) return;
    this.activeIndex = id;
    this.updateActiveItemStates();
  }
  /**
   * Updates arrow visibility based on scroll position.
   * Uses `newShowLeft` and `newShowRight` to avoid unnecessary updates.
   * Only triggers update if visibility changes.
   */
  updateArrowVisibility() {
    if (!this.navScroll) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.navScroll;
    const newShowLeft = scrollLeft > 0;
    const newShowRight = scrollLeft + clientWidth < scrollWidth - 1;
    if (this.showLeft !== newShowLeft || this.showRight !== newShowRight) {
      this.showLeft = newShowLeft;
      this.showRight = newShowRight;
      this.requestUpdate();
    }
  }
  scrollNav(direction) {
    if (!this.navScroll) return;
    const scrollAmount = 150;
    this.navScroll.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }
};
__decorate([property({ type: Boolean, reflect: true })], ShNavGroup.prototype, 'condensed', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-nav-label' })],
  ShNavGroup.prototype,
  'accessibleNavLabel',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'active-index' })],
  ShNavGroup.prototype,
  'activeIndex',
  void 0
);
__decorate(
  [queryAssignedElements({ selector: 'sh-nav-item' })],
  ShNavGroup.prototype,
  'items',
  void 0
);
__decorate([query('.nav-scroll')], ShNavGroup.prototype, 'navScroll', void 0);
__decorate([query('.nav-arrow-right')], ShNavGroup.prototype, 'rightArrow', void 0);
__decorate([state()], ShNavGroup.prototype, 'showLeft', void 0);
__decorate([state()], ShNavGroup.prototype, 'showRight', void 0);
ShNavGroup = __decorate([customElement('sh-nav-group')], ShNavGroup);
export { ShNavGroup };
